<template>
    <!-- Form -->
    <form @submit.prevent="handleSubmit" class="p-6 max-w-full overflow-x-hidden">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6 w-full min-w-0">
            <!-- Left Side: Event Details -->
            <div class="space-y-4 min-w-0">
                <!-- Title -->
        <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {{ $t('schedule.eventForm.title') }}
            </label>
            <input
                v-model="form.title"
                type="text"
                required
                :class="[
                    'w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-indigo-500 dark:bg-gray-700 dark:text-white',
                    hasFieldError('title') 
                        ? 'border-red-500 dark:border-red-400' 
                        : 'border-gray-300 dark:border-gray-600'
                ]"
                :placeholder="$t('schedule.eventForm.placeholders.title')"
                @blur="validateField('title', form.title)"
            />
            <p v-if="hasFieldError('title')" class="mt-1 text-sm text-red-600 dark:text-red-400">
                {{ getFieldError('title') }}
            </p>
        </div>

        <!-- Start Date & Time -->
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {{ $t('schedule.eventForm.startDate') }}
                </label>
                <Datepicker
                    v-model="form.date"
                    :language="currentLanguageConfig"
                    :format="customFormat"
                    :typeable="true"
                    :input-class="[
                        'w-full pl-10 pr-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-indigo-500 dark:bg-gray-700 dark:text-white',
                        (hasFieldError('date') || hasDateRangeError())
                            ? 'border-red-500 dark:border-red-400' 
                            : 'border-gray-300 dark:border-gray-600'
                    ].join(' ')"
                    :disabled-dates="{ to: minDateObj }"
                    @selected="validateField('date', form.date); syncEndDate()"
                    @input="syncEndDate()"
                />
                <p v-if="hasFieldError('date')" class="mt-1 text-sm text-red-600 dark:text-red-400">
                    {{ getFieldError('date') }}
                </p>
            </div>
            <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {{ $t('schedule.eventForm.startTime') }}
                </label>
                <input
                    v-model="form.startTime"
                    type="time"
                    :class="[
                        'w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-indigo-500 dark:bg-gray-700 dark:text-white',
                        hasTimeRangeError() 
                            ? 'border-red-500 dark:border-red-400' 
                            : 'border-gray-300 dark:border-gray-600'
                    ]"
                    @blur="handleStartTimeChange"
                    @input="handleStartTimeChange"
                />
            </div>
        </div>

        <!-- End Date & Time -->
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {{ $t('schedule.eventForm.endDate') }}
                </label>
                <Datepicker
                    v-model="form.endDate"
                    :language="currentLanguageConfig"
                    :format="customFormat"
                    :typeable="true"
                    :input-class="[
                        'w-full pl-10 pr-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-indigo-500 dark:bg-gray-700 dark:text-white',
                        (hasFieldError('endDate') || hasDateRangeError())
                            ? 'border-red-500 dark:border-red-400' 
                            : 'border-gray-300 dark:border-gray-600'
                    ].join(' ')"
                    :disabled-dates="{ to: formDateObj || minDateObj }"
                    @selected="validateField('endDate', form.endDate)"
                    @input="validateTimeRange"
                />
            </div>
            <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {{ $t('schedule.eventForm.endTime') }}
                </label>
                <input
                    v-model="form.endTime"
                    type="time"
                    :class="[
                        'w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-indigo-500 dark:bg-gray-700 dark:text-white',
                        hasTimeRangeError() 
                            ? 'border-red-500 dark:border-red-400' 
                            : 'border-gray-300 dark:border-gray-600'
                    ]"
                    @blur="validateTimeRange"
                    @input="validateTimeRange"
                />
            </div>
        </div>
        <!-- Range Error Messages -->
        <div v-if="hasDateRangeError()">
            <p class="text-sm text-red-600 dark:text-red-400">
                {{ $t('schedule.eventForm.errors.endDate') }}
            </p>
        </div>
        <div v-if="hasTimeRangeError()">
            <p class="text-sm text-red-600 dark:text-red-400">
                {{ $t('schedule.eventForm.errors.endTime') }}
            </p>
        </div>
        <!-- Address -->
        <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {{ $t('schedule.eventForm.address') }}
            </label>
            <MapboxAddressSearch
                v-model="form.address"
                :placeholder="$t('schedule.eventForm.placeholders.address')"
            />
        </div>

        <!-- Livestream Link -->
        <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {{ $t('schedule.eventForm.livestreamLink') }}
            </label>
            <input
                v-model="form.livestreamLink"
                type="url"
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                :placeholder="$t('schedule.eventForm.placeholders.livestream')"
            />
        </div>
        <!-- Category -->
        <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {{ $t('schedule.eventForm.category') }}
            </label>
            <div class="flex items-center space-x-2">
                <div 
                    class="w-4 h-4 rounded-full flex-shrink-0"
                    :class="getCategoryColorClass(form.category)"
                    :style="getCategoryColorStyle(form.category)"
                ></div>
                <select
                    v-model="form.category"
                    required
                    class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                >
                    <option selected value="">{{ $t('schedule.eventForm.placeholders.category') }}</option>
                    <option v-for="category in props.categories" :key="category.id" :value="category.id">
                        {{ $te(`schedule.categories.${category.name}`) ? $t(`schedule.categories.${category.name}`) : category.name }}
                    </option>
                </select>
            </div>
        </div>

        <!-- Description -->
        <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {{ $t('schedule.eventForm.description') }}
            </label>
            <textarea
                v-model="form.description"
                rows="3"
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                :placeholder="$t('schedule.eventForm.placeholders.description')"
            ></textarea>
        </div>

            </div>
            
            <!-- Right Side: Playlists & Event Participants -->
            <div class="space-y-6 min-w-0 md:border-l md:border-t-0 border-t border-gray-200 dark:border-gray-700 md:pl-6 pt-4 md:pt-0">
                <!-- Playlists Section -->
                <div class="relative playlist-section">
                    <div class="flex items-center justify-between mb-2">
                        <label class="block text-sm font-medium text-gray-900 dark:text-white">
                            {{ $t('schedule.eventForm.playlists') }}
                        </label>
                        <button
                            type="button"
                            @click="openPlaylistModal"
                            class="inline-flex items-center gap-1.5 text-xs font-semibold text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300 transition-colors"
                        >
                            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                            </svg>
                            <span>{{ $t('schedule.eventForm.browseAllPlaylists') }}</span>
                        </button>
                    </div>
                    
                    <!-- Autocomplete Search Input -->
                    <div class="relative">
                        <div class="absolute inset-y-0 start-0 flex items-center ps-3 pointer-events-none">
                            <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                            </svg>
                        </div>
                        <input
                            type="text"
                            v-model="playlistSearchQuery"
                            @focus="showPlaylistDropdown = true"
                            @input="handlePlaylistSearchInput"
                            class="w-full pl-9 pr-8 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                            :placeholder="$t('schedule.eventForm.searchPlaylists')"
                        />
                        <button
                            v-if="playlistSearchQuery"
                            type="button"
                            @click="playlistSearchQuery = ''; showPlaylistDropdown = false"
                            class="absolute inset-y-0 end-0 flex items-center pe-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                        >
                            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>

                        <!-- Autocomplete Dropdown -->
                        <div
                            v-if="showPlaylistDropdown && (playlistSearchLoading || autocompletePlaylists.length > 0 || playlistSearchQuery)"
                            class="absolute top-full left-0 right-0 z-50 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-52 overflow-y-auto"
                        >
                            <div v-if="playlistSearchLoading" class="p-3 text-center text-xs text-gray-500 dark:text-gray-400">
                                <Loading class="w-full" />
                            </div>
                            <div v-else-if="autocompletePlaylists.length === 0" class="p-3 text-center text-xs text-gray-500 dark:text-gray-400">
                                {{ $t('schedule.eventForm.noPlaylistsFound') }}
                            </div>
                            <ul v-else class="divide-y divide-gray-100 dark:divide-gray-700">
                                <li
                                    v-for="pl in autocompletePlaylists"
                                    :key="'autocomplete-' + (pl.id || pl._id)"
                                    @click="selectPlaylistFromAutocomplete(pl)"
                                    class="p-2.5 hover:bg-purple-50 dark:hover:bg-purple-900/30 cursor-pointer flex items-center justify-between transition-colors"
                                >
                                    <div class="flex items-center space-x-2 truncate">
                                        <svg class="w-4 h-4 text-purple-600 dark:text-purple-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                                        </svg>
                                        <span class="text-sm font-medium text-gray-900 dark:text-white truncate">{{ pl.name }}</span>
                                    </div>
                                    <span class="text-xs text-gray-400 dark:text-gray-500 ml-2 flex-shrink-0">
                                        {{ pl.songs ? pl.songs.length : 0 }} {{ $t('playlistEditor.songs') }}
                                    </span>
                                </li>
                            </ul>
                        </div>
                    </div>

                    <!-- Selected Playlists Chips -->
                    <div class="flex flex-wrap gap-2 mt-3 min-h-[32px]">
                        <div
                            v-for="pl in form.playlists"
                            :key="'form-playlist-' + (pl.id || pl._id)"
                            class="inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-700/50 rounded-full text-xs font-medium max-w-full shadow-sm"
                        >
                            <svg class="w-3.5 h-3.5 text-purple-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                            </svg>
                            <span class="truncate max-w-[180px]">{{ pl.name }}</span>
                            <button
                                type="button"
                                @click="handleRemovePlaylist(pl)"
                                class="hover:bg-purple-200 dark:hover:bg-purple-800 rounded-full p-0.5 text-purple-600 dark:text-purple-300 transition-colors ml-0.5"
                                title="Remove"
                            >
                                <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Event Participants -->
                <div class="border-t border-gray-200 dark:border-gray-700 pt-4">
                    <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-4">{{ $t('schedule.eventForm.participants') }}</h3>
                    <EventParticipantSearch 
                        ref="eventParticipantSearch"
                        :exclude-users="excludeUserIds"
                        :exclude-teams="excludeTeamIds"
                        :initial-users="form.participants?.users || []"
                        :initial-teams="form.participants?.teams || []"
                        @participants-updated="handleParticipantsUpdate"
                        @add-participant="handleAddParticipant"
                        @remove-participant="handleRemoveParticipant"
                    />
                </div>
            </div>
        </div>

        <!-- Actions -->
        <div class="flex space-x-3 pt-6 mt-6 border-t border-gray-200 dark:border-gray-700">
            <button
                type="button"
                @click="closeModal"
                class="flex-1 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
            >
                {{ $t('schedule.eventForm.cancel') }}
            </button>
            <button
                type="button"
                @click="handleSubmit"
                :disabled="isSubmitting || !isFormValidForSubmit"
                class="flex-1 px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
                {{ isSubmitting ? $t('schedule.eventForm.saving') : (isEditing ? $t('schedule.eventForm.updateEvent') : $t('schedule.eventForm.createEvent')) }}
            </button>
        </div>

        <!-- Second Modal for Playlist Selection with Infinite/Virtual Scrolling -->
        <PlaylistSelectModal
            ref="playlistSelectModalRef"
            :model-value="form.playlists"
            @update:model-value="handleModalPlaylistsUpdate"
        />
    </form>
</template>

<script setup>
import { ref, watch, computed, onMounted, onUnmounted, defineProps, defineEmits } from 'vue'
import { useStore } from 'vuex'
import EventParticipantSearch from './EventParticipantSearch.vue'
import MapboxAddressSearch from './MapboxAddressSearch.vue'
import PlaylistSelectModal from './PlaylistSelectModal.vue'
import Loading from '@/components/TriadLoad.vue'
import { useModelValidation } from '../../composables/useValidation.js'
import { isTailwindColor } from '@/utilities/color'
import ScheduleService from '../../service/schedule.service'
import PlaylistService from '../../service/playlist.service'
import Datepicker from 'vuejs3-datepicker'
import { useI18n } from 'vue-i18n'
import { debounce } from '@/service/debounce.js'

// Props
const props = defineProps({
    show: {
        type: Boolean,
        default: false
    },
    event: {
        type: Object,
        default: null
    },
    categories: {
        type: Array,
        default: () => {
            return [];
        }
    }
})

const eventParticipantSearch = ref(null)
const playlistSelectModalRef = ref(null)
const availablePlaylists = ref([])
const playlistSearchQuery = ref('')
const playlistSearchLoading = ref(false)
const showPlaylistDropdown = ref(false)
const autocompletePlaylists = ref([])

// Emits
const emit = defineEmits(['close', 'save'])

const toastStore = useStore()
const { locale, t } = useI18n()

const currentLanguageConfig = computed(() => {
    const loc = locale.value || 'en';
    if (loc.startsWith('pt')) return 'pt';
    if (loc.startsWith('es')) return 'es';
    if (loc.startsWith('fr')) return 'fr';
    return 'en';
})

const customFormat = 'yyyy-MM-dd';

// Validation
const {
    validateField,
    validateWithToast,
    clearErrors,
    getFieldError,
    hasFieldError,
    isSubmitting,
    isFormValid
} = useModelValidation('Event', {
    showToast: true,
    validateOnChange: true
})

// Form data
const form = ref({
    title: '',
    date: '',
    endDate: '',
    startTime: '',
    endTime: '',
    category: '',
    description: '',
    address: '',
    livestreamLink: '',
    playlists: [],
    participants: {
        users: [],
        teams: []
    }
})

const fetchPlaylists = async () => {
    try {
        const result = await PlaylistService.fetchAll({ force: true })
        if (Array.isArray(result)) {
            availablePlaylists.value = result
        } else if (result && Array.isArray(result.data)) {
            availablePlaylists.value = result.data
        }
    } catch (err) {
        console.error('Error fetching playlists for event form:', err)
    }
}

// Time range validation state
const timeRangeError = ref(false)
const dateRangeError = ref(false)

// Computed
const isEditing = computed(() => !!(props.event?.id || props.event?._id))

const minDateObj = computed(() => {
    if (isEditing.value) return undefined;
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), today.getDate());
});

const formDateObj = computed(() => {
    if (!form.value.date) return undefined;
    return form.value.date instanceof Date ? form.value.date : new Date(`${form.value.date}T00:00:00`);
});

// Time range validation methods
const syncEndDate = () => {
    if (form.value.date && (!form.value.endDate || form.value.endDate < form.value.date)) {
        form.value.endDate = form.value.date;
    }
    validateTimeRange();
}

const getLocalDateString = (dateVal) => {
    if (!dateVal) return dateVal;
    if (!(dateVal instanceof Date)) return dateVal;
    const year = dateVal.getFullYear();
    const month = String(dateVal.getMonth() + 1).padStart(2, '0');
    const day = String(dateVal.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const validateTimeRange = () => {
    dateRangeError.value = false;
    timeRangeError.value = false;

    if (form.value.date && form.value.endDate) {
        // Ensure dates are parsed correctly whether they are strings or Date objects
        const startDateObj = form.value.date instanceof Date ? form.value.date : new Date(`${form.value.date}T00:00:00`);
        const endDateObj = form.value.endDate instanceof Date ? form.value.endDate : new Date(`${form.value.endDate}T00:00:00`);
        
        const startDay = new Date(startDateObj.getFullYear(), startDateObj.getMonth(), startDateObj.getDate());
        const endDay = new Date(endDateObj.getFullYear(), endDateObj.getMonth(), endDateObj.getDate());
        const diffTime = endDay - startDay;
        const diffDays = diffTime / (1000 * 60 * 60 * 24);

        if (diffDays < 0 || diffDays > 1) {
            dateRangeError.value = true;
        }
    }

    if (form.value.startTime && form.value.endTime && form.value.date) {
        const dateStr = getLocalDateString(form.value.date);
        const endDateStr = form.value.endDate ? getLocalDateString(form.value.endDate) : dateStr;
        const startTime = new Date(`${dateStr}T${form.value.startTime}`)
        const endTime = new Date(`${endDateStr}T${form.value.endTime}`)
        timeRangeError.value = endTime <= startTime
    }
}

const handleStartTimeChange = () => {
    if (form.value.startTime && form.value.date) {
        const dateStr = getLocalDateString(form.value.date);
        const startDateTime = new Date(`${dateStr}T${form.value.startTime}`);
        // Add 2 hours to the newly picked start time
        const endDateTime = new Date(startDateTime.getTime() + 2 * 60 * 60 * 1000);
        
        // Update both end date and end time
        form.value.endDate = new Date(endDateTime.getFullYear(), endDateTime.getMonth(), endDateTime.getDate());
        form.value.endTime = endDateTime.toTimeString().slice(0, 5);
    }
    validateTimeRange();
}

const hasDateRangeError = () => {
    return dateRangeError.value
}

const hasTimeRangeError = () => {
    return timeRangeError.value
}

// Check if form is valid for submission
const isFormValidForSubmit = computed(() => {
    const eventData = {
        title: form.value.title,
        date: getLocalDateString(form.value.date),
        endDate: form.value.endDate ? getLocalDateString(form.value.endDate) : getLocalDateString(form.value.date),
        startTime: form.value.startTime,
        endTime: form.value.endTime,
        category: form.value.category,
        description: form.value.description,
        address: form.value.address,
        livestreamLink: form.value.livestreamLink,
        participants: form.value.participants
    }
    return isFormValid(eventData) && !timeRangeError.value && !dateRangeError.value
})

const excludeUserIds = computed(() => {
    return form.value.participants.users.map(user => user.id)
})

const excludeTeamIds = computed(() => {
    return form.value.participants.teams.map(team => team._id)
})

const getCategoryColorStyle = (categoryId) => {
    const category = props.categories.find(c => c.id === categoryId)
    const color = category ? category.color : 'gray'
    if (isTailwindColor(color)) {
        return {}
    }
    return {
        backgroundColor: color
    }
}

const getCategoryColorClass = (categoryId) => {
    const category = props.categories.find(c => c.id === categoryId)
    const color = category ? category.color : 'gray'
    if (isTailwindColor(color)) {
        return `bg-${color}-500`
    }
    return ''
}

// Methods
const resetForm = () => {
    const now = new Date();
    const startDate = new Date(now.getTime() + 60 * 60 * 1000); // +1 hour
    const endDate = new Date(now.getTime() + 2 * 60 * 60 * 1000); // +2 hours

    playlistSearchQuery.value = ''
    showPlaylistDropdown.value = false
    autocompletePlaylists.value = []
    form.value = {
        title: '',
        date: startDate,
        endDate: endDate,
        startTime: startDate.toTimeString().slice(0, 5),
        endTime: endDate.toTimeString().slice(0, 5),
        category: props.categories.length > 0 ? props.categories[0].id: '',
        description: '',
        address: '',
        livestreamLink: '',
        playlists: [],
        participants: {
            users: [],
            teams: []
        }
    }
    // Clear validation errors when resetting form
    if (eventParticipantSearch.value) {
        eventParticipantSearch.value.reset()
    }
    clearErrors()
    timeRangeError.value = false
    dateRangeError.value = false
}

const closeModal = () => {
    resetForm()
    emit('close')
}

const handleParticipantsUpdate = (participants) => {
    form.value.participants = participants
}

const handlePlaylistSearchInput = debounce(async () => {
    const query = playlistSearchQuery.value.trim().toLowerCase()
    if (!query) {
        autocompletePlaylists.value = []
        playlistSearchLoading.value = false
        return
    }

    playlistSearchLoading.value = true
    showPlaylistDropdown.value = true
    try {
        const res = await PlaylistService.fetchPaginated({
            page: 1,
            size: 10,
            search: query
        })
        const items = res.data || (Array.isArray(res) ? res : [])
        const selectedIds = new Set((form.value.playlists || []).map(p => p.id || p._id))
        autocompletePlaylists.value = items.filter(p => !selectedIds.has(p.id || p._id))
    } catch (err) {
        console.error('Error searching playlists in autocomplete:', err)
        // Fallback to in-memory filter
        const selectedIds = new Set((form.value.playlists || []).map(p => p.id || p._id))
        autocompletePlaylists.value = availablePlaylists.value.filter(p => 
            !selectedIds.has(p.id || p._id) && 
            p.name && 
            p.name.toLowerCase().includes(query)
        )
    } finally {
        playlistSearchLoading.value = false
    }
}, 300)

const selectPlaylistFromAutocomplete = async (playlist) => {
    if (!playlist) return
    const targetId = playlist.id || playlist._id
    if (!form.value.playlists) {
        form.value.playlists = []
    }
    if (!form.value.playlists.some(p => (p.id || p._id) === targetId)) {
        form.value.playlists.push(playlist)
        if (isEditing.value && props.event?.id) {
            try {
                await ScheduleService.addPlaylist(props.event.id, targetId)
                toastStore.dispatch('addMessage', { message: t('schedule.playlistLinked', 'Playlist linked'), icon: 'success', ttl: 2000 })
            } catch (error) {
                console.error('Error linking playlist:', error)
                toastStore.dispatch('addMessage', { message: t('schedule.failedToLinkPlaylist', 'Failed to link playlist'), icon: 'error', ttl: 5000 })
            }
        }
    }
    playlistSearchQuery.value = ''
    showPlaylistDropdown.value = false
    autocompletePlaylists.value = []
}

const openPlaylistModal = () => {
    if (playlistSelectModalRef.value) {
        playlistSelectModalRef.value.open(form.value.playlists || [])
    }
}

const handleModalPlaylistsUpdate = async (updatedPlaylists) => {
    const oldPlaylists = [...(form.value.playlists || [])]
    form.value.playlists = [...updatedPlaylists]

    if (isEditing.value && props.event?.id) {
        const oldIds = new Set(oldPlaylists.map(p => p.id || p._id))
        const newIds = new Set(updatedPlaylists.map(p => p.id || p._id))

        // Find added
        for (const pl of updatedPlaylists) {
            const id = pl.id || pl._id
            if (!oldIds.has(id)) {
                try {
                    await ScheduleService.addPlaylist(props.event.id, id)
                } catch (e) {
                    console.error('Error adding playlist from modal:', e)
                }
            }
        }

        // Find removed
        for (const pl of oldPlaylists) {
            const id = pl.id || pl._id
            if (!newIds.has(id)) {
                try {
                    await ScheduleService.removePlaylist(props.event.id, id)
                } catch (e) {
                    console.error('Error removing playlist from modal:', e)
                }
            }
        }
    }
}

const handleRemovePlaylist = async (playlist) => {
    const playlistId = playlist.id || playlist._id
    form.value.playlists = (form.value.playlists || []).filter(p => (p.id || p._id) !== playlistId)

    if (isEditing.value && props.event?.id) {
        try {
            await ScheduleService.removePlaylist(props.event.id, playlistId)
            toastStore.dispatch('addMessage', { message: t('schedule.playlistUnlinked', 'Playlist unlinked'), icon: 'success', ttl: 2000 })
        } catch (error) {
            console.error('Error unlinking playlist:', error)
            toastStore.dispatch('addMessage', { message: t('schedule.failedToUnlinkPlaylist', 'Failed to unlink playlist'), icon: 'error', ttl: 5000 })
        }
    }
}

const handleAddParticipant = async ({ id, type }) => {
    // If editing existing event, add immediately
    if (isEditing.value && props.event?.id) {
        try {
            await ScheduleService.addParticipant(props.event.id, id, type);
            toastStore.dispatch('addMessage', { message: t('schedule.participantAdded', 'Participant added'), icon: 'success', ttl: 2000 });
        } catch (error) {
            console.error('Error adding participant:', error);
            toastStore.dispatch('addMessage', { message: t('schedule.failedToAddParticipant', 'Failed to add participant'), icon: 'error', ttl: 5000 });
        }
    }
}

const handleRemoveParticipant = async ({ id }) => {
    // If editing existing event, remove immediately
    if (isEditing.value && props.event?.id) {
        try {
            await ScheduleService.removeParticipant(props.event.id, id);
            toastStore.dispatch('addMessage', { message: t('schedule.participantRemoved', 'Participant removed'), icon: 'success', ttl: 2000 });
        } catch (error) {
            console.error('Error removing participant:', error);
            toastStore.dispatch('addMessage', { message: t('schedule.failedToRemoveParticipant', 'Failed to remove participant'), icon: 'error', ttl: 5000 });
        }
    }
}

const handleSubmit = async () => {
    // Validate time range first
    validateTimeRange()
    
    if (timeRangeError.value) {
        return;
    }
    
    const eventData = {
        id: props.event?.id || props.event?._id,
        title: form.value.title,
        date: getLocalDateString(form.value.date),
        startTime: form.value.startTime,
        endTime: form.value.endTime,
        category: form.value.category,
        description: form.value.description,
        address: form.value.address,
        livestreamLink: form.value.livestreamLink,
        playlist_ids: (form.value.playlists || []).map(p => p.id || p._id).filter(Boolean),
        playlists: form.value.playlists || []
    }
    
    // Add participants only during creation. During updates, they are handled individually.
    if (!isEditing.value) {
        eventData.participants = form.value.participants;
    }
    
    // Validate the form data
    if (!validateWithToast(eventData)) {
        return;
    }
    
    // If validation passes, emit the save event
    const startDateStr = getLocalDateString(form.value.date)
    const endDateStr = form.value.endDate ? getLocalDateString(form.value.endDate) : startDateStr
    const localMidnightDate = new Date(`${startDateStr}T00:00:00`);
    eventData.date = localMidnightDate;
    eventData.startTime = form.value.startTime ? new Date(`${startDateStr}T${form.value.startTime}`) : null;
    eventData.endTime = form.value.endTime ? new Date(`${endDateStr}T${form.value.endTime}`) : null;
    emit('save', eventData)
    closeModal()
}

// Click outside handler for autocomplete
const handleClickOutside = (event) => {
    if (!event.target.closest('.playlist-section')) {
        showPlaylistDropdown.value = false
    }
}

onMounted(() => {
    document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside)
})

// Watch for event changes to populate form
watch(() => props.event, async (newEvent) => {
    fetchPlaylists()
    if (newEvent) {
        let startDate = null
        let endDate = null

        if (newEvent.startTime) {
            const s = newEvent.startTime instanceof Date ? newEvent.startTime : new Date(newEvent.startTime)
            startDate = new Date(s.getFullYear(), s.getMonth(), s.getDate())
        } else if (newEvent.date) {
            if (newEvent.date instanceof Date) {
                startDate = new Date(newEvent.date.getFullYear(), newEvent.date.getMonth(), newEvent.date.getDate())
            } else if (typeof newEvent.date === 'string') {
                const [y, m, d] = newEvent.date.slice(0, 10).split('-').map(Number)
                startDate = new Date(y, m - 1, d)
            }
        }
        if (!startDate) {
            startDate = new Date()
        }

        if (newEvent.endTime) {
            const e = newEvent.endTime instanceof Date ? newEvent.endTime : new Date(newEvent.endTime)
            endDate = new Date(e.getFullYear(), e.getMonth(), e.getDate())
        } else {
            endDate = startDate
        }

        const startTimeStr = newEvent.startTime ? (newEvent.startTime instanceof Date ? newEvent.startTime : new Date(newEvent.startTime)).toTimeString().slice(0, 5) : ''
        const endTimeStr = newEvent.endTime ? (newEvent.endTime instanceof Date ? newEvent.endTime : new Date(newEvent.endTime)).toTimeString().slice(0, 5) : ''

        form.value = {
            title: newEvent.title || '',
            date: startDate,
            endDate: endDate,
            startTime: startTimeStr,
            endTime: endTimeStr,
            category: newEvent.categoryId || newEvent.category || (props.categories.length > 0 ? props.categories[0].id : ''),
            description: newEvent.description || '',
            address: newEvent.address || '',
            livestreamLink: newEvent.livestreamLink || '',
            playlists: newEvent.playlists ? [...newEvent.playlists] : [],
            participants: {
                users: newEvent.participants?.users || [],
                teams: newEvent.participants?.teams || []
            }
        }

        // Fetch latest participants from API if editing
        const eventId = newEvent.id || newEvent._id
        if (eventId) {
            try {
                const participants = await ScheduleService.fetchParticipants(eventId);
                if (participants && !participants.error) {
                    form.value.participants = {
                        users: participants.users || [],
                        teams: participants.teams || []
                    };
                }
            } catch (error) {
                console.error('Error fetching participants:', error);
            }
        }
    } else {
        resetForm()
    }
}, { immediate: true })

// Watch for show prop to reset form when modal opens
watch(() => props.show, (show) => {
    if (!show) {
        resetForm()
    } else {
        fetchPlaylists()
    }
})
</script> 